import { useState, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  User, Mail, Phone, Calendar, GraduationCap, MapPin, Loader2, Save, QrCode,
  Camera, Upload, FileText, Trash2, Download, AlertCircle, Image, Book, KeyRound
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Student document types
const STUDENT_DOCUMENT_TYPES = {
  "student_photo": { label: "Student Photo", required: true, max_size: 0.1 },
  "birth_certificate": { label: "Birth Certificate", required: true, max_size: 0.3 },
  "aadhaar_student": { label: "Student Aadhaar Card", required: true, max_size: 0.3 },
  "previous_marksheet": { label: "Previous Class Marksheet", required: false, max_size: 0.3 },
  "transfer_certificate": { label: "Transfer Certificate", required: false, max_size: 0.3 },
  "character_certificate": { label: "Character Certificate", required: false, max_size: 0.3 },
  "caste_certificate": { label: "Caste Certificate", required: false, max_size: 0.3 },
  "medical_certificate": { label: "Medical Certificate", required: false, max_size: 0.3 },
};

const StudentProfilePage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  
  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });
  
  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    date_of_birth: '',
    class_name: '',
    section: '',
    roll_number: '',
    admission_number: '',
    admission_date: '',
    father_name: '',
    mother_name: '',
    parent_phone: '',
    profile_photo_url: ''
  });
  
  const [documents, setDocuments] = useState([]);
  const [missingDocs, setMissingDocs] = useState([]);

  useEffect(() => {
    fetchProfile();
    fetchDocuments();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/students/my-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      if (user) {
        setProfile(prev => ({
          ...prev,
          name: user.name || '',
          email: user.email || ''
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/my-student-documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
      setMissingDocs(response.data.missing_required || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const studentCode = profile.admission_number || `STU${user?.id?.substring(0, 8).toUpperCase() || '00000000'}`;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/students/my-profile`, {
        phone: profile.phone,
        address: profile.address
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Profile Photo Upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error('Image must be less than 500KB');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/documents/my-student-profile-photo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setProfile(prev => ({ ...prev, profile_photo_url: response.data.photo_url }));
      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  // Document Upload
  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedDocType) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', selectedDocType);
    
    try {
      await axios.post(
        `${API_URL}/api/documents/my-student-documents/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Document uploaded successfully!');
      setUploadDialogOpen(false);
      setSelectedDocType('');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="My Profile">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Profile">
      <div className="space-y-6">
        {/* Profile Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-r from-pink-500 to-rose-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  {/* Profile Photo */}
                  <div className="relative group">
                    <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                      {profile.profile_photo_url ? (
                        <img 
                          src={`${API_URL}${profile.profile_photo_url}`} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-12 h-12 text-white" />
                      )}
                    </div>
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      disabled={uploading}
                    >
                      {uploading ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : (
                        <Camera className="w-6 h-6" />
                      )}
                    </button>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </div>
                  
                  <div>
                    <h2 className="text-2xl font-bold">{profile.name || user?.name || 'Student'}</h2>
                    <p className="text-pink-100">{profile.class_name} {profile.section && `- ${profile.section}`}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <Badge variant="secondary" className="bg-white/20 text-white">
                        <GraduationCap className="h-3 w-3 mr-1" /> Student
                      </Badge>
                      {profile.roll_number && (
                        <Badge variant="secondary" className="bg-white/20 text-white">
                          Roll: {profile.roll_number}
                        </Badge>
                      )}
                      {profile.admission_number && (
                        <Badge variant="secondary" className="bg-white/20 text-white">
                          Adm: {profile.admission_number}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                <Button 
                  variant="secondary" 
                  className="bg-white/20 hover:bg-white/30 text-white"
                  onClick={() => setQrDialogOpen(true)}
                >
                  <QrCode className="h-5 w-5 mr-2" /> My QR Code
                </Button>
                <Button 
                  variant="secondary" 
                  className="bg-white/20 hover:bg-white/30 text-white"
                  onClick={() => setChangePasswordOpen(true)}
                  data-testid="change-password-btn"
                >
                  <KeyRound className="h-5 w-5 mr-2" /> Change Password
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="profile" className="gap-2">
              <User className="h-4 w-4" /> Profile Details
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-2">
              <FileText className="h-4 w-4" /> My Documents
              {missingDocs.length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {missingDocs.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Personal Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input value={profile.name || ''} disabled className="bg-muted" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={profile.email || ''} disabled className="bg-muted" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input 
                      value={profile.phone || ''} 
                      onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div>
                    <Label>Date of Birth</Label>
                    <Input value={profile.date_of_birth || ''} disabled className="bg-muted" />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input 
                      value={profile.address || ''} 
                      onChange={(e) => setProfile(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Enter address"
                    />
                  </div>
                  <Button onClick={handleSave} disabled={saving} className="w-full">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </CardContent>
              </Card>

              {/* Academic & Family Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Academic & Family Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground text-sm">Class</Label>
                      <p className="font-medium">{profile.class_name || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-sm">Section</Label>
                      <p className="font-medium">{profile.section || '-'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground text-sm">Roll Number</Label>
                      <p className="font-medium">{profile.roll_number || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-sm">Admission No.</Label>
                      <p className="font-medium">{profile.admission_number || '-'}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-sm">Father's Name</Label>
                    <p className="font-medium">{profile.father_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-sm">Mother's Name</Label>
                    <p className="font-medium">{profile.mother_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-sm">Parent's Phone</Label>
                    <p className="font-medium">{profile.parent_phone || '-'}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">My Documents</CardTitle>
                <Button onClick={() => setUploadDialogOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" /> Upload Document
                </Button>
              </CardHeader>
              <CardContent>
                {missingDocs.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-amber-700 mb-2">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">Missing Required Documents ({missingDocs.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {missingDocs.map((doc) => (
                        <Badge key={doc.type} variant="outline" className="text-amber-700 border-amber-300">
                          {doc.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {documents.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No documents uploaded yet</p>
                    <Button className="mt-4" onClick={() => setUploadDialogOpen(true)}>
                      <Upload className="h-4 w-4 mr-2" /> Upload Your First Document
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {documents.map((doc) => (
                      <div key={doc.id} className="border rounded-lg p-4 flex items-start gap-3">
                        <div className="bg-pink-100 dark:bg-pink-900/30 p-2 rounded">
                          <FileText className="h-5 w-5 text-pink-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{doc.document_label}</p>
                          <p className="text-xs text-muted-foreground">{doc.file_size_kb} KB</p>
                        </div>
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => window.open(`${API_URL}/api/documents/download/${doc.id}`, '_blank')}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* QR Code Dialog */}
        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>My QR Code</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center py-6">
              <div className="bg-white p-4 rounded-lg">
                <QRCode value={studentCode} size={200} />
              </div>
              <p className="mt-4 text-lg font-mono font-bold">{studentCode}</p>
              <p className="text-sm text-muted-foreground">Scan for attendance</p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Upload Document Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Document Type</Label>
                <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STUDENT_DOCUMENT_TYPES).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        {value.label} {value.required && <span className="text-red-500">*</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedDocType && (
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <input
                    ref={docInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.webp"
                    onChange={handleDocumentUpload}
                    className="hidden"
                  />
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-2">
                    Max size: {STUDENT_DOCUMENT_TYPES[selectedDocType]?.max_size * 1024} KB
                  </p>
                  <Button onClick={() => docInputRef.current?.click()} disabled={uploading}>
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Select File
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Change Password Dialog */}
        <ChangePasswordDialog 
          open={changePasswordOpen} 
          onOpenChange={setChangePasswordOpen}
          getAuthHeader={getAuthHeader}
        />
      </div>
    </DashboardLayout>
  );
};

export default StudentProfilePage;
